import { useState } from 'react'
import { ChevronDown, ChevronUp, Plus, Settings2, Trash2 } from 'lucide-react'
import type { BlocoKanbanColumn } from '@/types/bloco'
import { DEFAULT_KANBAN_COLUMN_COLORS } from './blocosKanbanUtils'

interface BlocoKanbanColumnsPanelProps {
  columns: BlocoKanbanColumn[]
  loading?: boolean
  onCreate: (name: string, accent: string) => Promise<unknown>
  onRename: (columnId: string, name: string) => Promise<void>
  onRemove: (columnId: string) => Promise<void>
  onReorder: (columnIds: string[]) => Promise<void>
}

export function BlocoKanbanColumnsPanel({
  columns,
  loading = false,
  onCreate,
  onRename,
  onRemove,
  onReorder,
}: BlocoKanbanColumnsPanelProps) {
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [accent, setAccent] = useState(DEFAULT_KANBAN_COLUMN_COLORS[1])

  const sorted = [...columns].sort((a, b) => a.sortOrder - b.sortOrder)

  const moveColumn = async (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction
    if (nextIndex < 0 || nextIndex >= sorted.length) return
    const ids = sorted.map((c) => c.id)
    const [removed] = ids.splice(index, 1)
    ids.splice(nextIndex, 0, removed)
    await onReorder(ids)
  }

  const handleCreate = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    await onCreate(trimmed, accent)
    setName('')
    setCreating(false)
  }

  return (
    <div className='rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] p-3'>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        className='flex w-full items-center justify-between gap-2 text-left'
      >
        <span className='flex items-center gap-2 text-xs font-medium text-text-muted'>
          <Settings2 size={14} />
          Personalizar colunas do Kanban
        </span>
        <span className='text-[11px] text-text-muted'>{sorted.length} colunas</span>
      </button>

      {open && (
        <div className='mt-3 space-y-2 border-t border-[var(--glass-border)] pt-3'>
          {sorted.map((column, index) => {
            const isSystem = column.kind === 'done' || column.kind === 'waiting'
            return (
              <div
                key={column.id}
                className='flex items-center gap-2 rounded-md border border-[var(--glass-border)] bg-bg-card px-2 py-1.5'
              >
                <span className='h-3 w-3 shrink-0 rounded-full' style={{ backgroundColor: column.accent }} />
                <input
                  defaultValue={column.name}
                  disabled={loading || column.kind === 'done'}
                  onBlur={(e) => {
                    const next = e.target.value.trim()
                    if (next && next !== column.name) void onRename(column.id, next)
                  }}
                  className='min-w-0 flex-1 bg-transparent text-sm text-text-primary outline-none disabled:opacity-60'
                />
                <div className='flex shrink-0 items-center gap-0.5'>
                  <button
                    type='button'
                    disabled={loading || index === 0}
                    onClick={() => void moveColumn(index, -1)}
                    className='rounded p-1 text-text-muted hover:bg-[var(--glass-bg)] disabled:opacity-30'
                    aria-label='Mover coluna para esquerda'
                  >
                    <ChevronUp size={14} className='rotate-[-90deg]' />
                  </button>
                  <button
                    type='button'
                    disabled={loading || index === sorted.length - 1}
                    onClick={() => void moveColumn(index, 1)}
                    className='rounded p-1 text-text-muted hover:bg-[var(--glass-bg)] disabled:opacity-30'
                    aria-label='Mover coluna para direita'
                  >
                    <ChevronDown size={14} className='rotate-[-90deg]' />
                  </button>
                  {!isSystem && (
                    <button
                      type='button'
                      disabled={loading}
                      onClick={() => {
                        if (window.confirm(`Remover coluna "${column.name}"? Tarefas serão movidas para outra coluna.`)) {
                          void onRemove(column.id)
                        }
                      }}
                      className='rounded p-1 text-text-muted hover:bg-red-500/10 hover:text-red-500 disabled:opacity-30'
                      aria-label={`Remover coluna ${column.name}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            )
          })}

          {creating ? (
            <div className='flex flex-wrap items-center gap-2 rounded-md border border-[var(--glass-border)] bg-bg-card p-2'>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder='Nome da coluna'
                className='min-w-[140px] flex-1 rounded border border-[var(--glass-border)] bg-transparent px-2 py-1 text-sm'
              />
              <div className='flex gap-1'>
                {DEFAULT_KANBAN_COLUMN_COLORS.slice(0, 5).map((color) => (
                  <button
                    key={color}
                    type='button'
                    onClick={() => setAccent(color)}
                    className={`h-6 w-6 rounded-full border-2 ${accent === color ? 'border-white' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    aria-label={`Cor ${color}`}
                  />
                ))}
              </div>
              <button
                type='button'
                onClick={() => void handleCreate()}
                className='rounded bg-[var(--brand-primary)] px-3 py-1 text-xs font-medium text-white'
              >
                Salvar
              </button>
              <button type='button' onClick={() => setCreating(false)} className='text-xs text-text-muted'>
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type='button'
              onClick={() => setCreating(true)}
              className='inline-flex items-center gap-1 rounded-md border border-dashed border-[var(--glass-border)] px-2 py-1 text-xs text-text-muted hover:text-text-primary'
            >
              <Plus size={12} />
              Nova coluna
            </button>
          )}

          <p className='text-[11px] leading-relaxed text-text-muted'>
            Cada usuário tem seu próprio quadro. Colunas <strong>Aguardando</strong> e <strong>Concluído</strong> são
            do sistema e não podem ser removidas.
          </p>
        </div>
      )}
    </div>
  )
}
